/**
 * THE BROWSER SIDE OF THE MODEL FLEET. No helper here turns a count into a
 * percentage: `11 of 14 read` is a fact, `78.6% accuracy` is a different claim
 * wearing the same digits, and nothing in this product measures accuracy.
 *
 * The types are declared again in `functions/api/models.ts` for the reason
 * given at the top of `src/api/payments.ts`.
 */

export type PinView = {
  readonly modelPin: string;
  readonly version: string | null;
  readonly readings: number;
  readonly lastAt: number | null;
};

export type SurfaceView = {
  readonly key: "round_debrief" | "jd_target" | "mock_interviewer" | "mock_reading";
  readonly label: string;
  readonly what: string;
  readonly table: string;
  readonly configuredModel: string;
  readonly configuredVersion: string | null;
  readonly maxAttempts: number;
  readonly total: number;
  readonly read: number;
  readonly pending: number;
  readonly givenUp: number;
  readonly attempts: number;
  readonly pins: readonly PinView[];
};

export type PromptView = {
  readonly key: string;
  readonly label: string;
  readonly source: string;
  readonly usedBy: readonly string[];
  readonly text: string;
  readonly kind: string | null;
};

export type VocabularyGroup = {
  readonly column: string;
  readonly values: readonly { readonly value: string; readonly count: number }[];
};

export type ModelsPayload = {
  readonly ok: true;
  readonly now: number;
  readonly surfaces: readonly SurfaceView[];
  readonly prompts: readonly PromptView[];
  /** How many skill ids the two reading prompts carry as their closed list. */
  readonly vocabulary: number;
  readonly vocabularyGroups: readonly VocabularyGroup[];
  readonly kinds: readonly { readonly key: string; readonly label: string }[];
  readonly kind: string;
};

/** A column name from the skills table, said the way a person reads it. */
export function groupLabel(column: string): string {
  return `${column.charAt(0).toUpperCase()}${column.slice(1).replace(/_/g, " ")}`;
}

export function valueLabel(value: string): string {
  return value.replace(/_/g, " ");
}

const WHEN = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

export function formatWhen(ms: number | null): string {
  return ms === null || ms <= 0 ? "never" : WHEN.format(new Date(ms));
}

/** `11 of 14 read`, or what to say when there are no rows at all. */
export function readTally(surface: SurfaceView): string {
  if (surface.total === 0) return "nothing to read yet";
  return `${surface.read} of ${surface.total} read`;
}

/**
 * A PIN THAT IS NOT THE CONSTANT THE CODE NOW CARRIES. Not a fault: it is the
 * ordinary state of any row read before the constant changed, and the whole
 * reason the pin is stored.
 */
export function driftedPins(surface: SurfaceView): readonly PinView[] {
  return surface.pins.filter((pin) => pin.modelPin !== surface.configuredModel);
}

export function totalReadings(surface: SurfaceView): number {
  return surface.pins.reduce((sum, pin) => sum + pin.readings, 0);
}

export class ApiError extends Error {}

export function modelsPath(kind: string | null): string {
  return kind === null || kind.length === 0
    ? "/api/models"
    : `/api/models?kind=${encodeURIComponent(kind)}`;
}

export async function loadModels(kind: string | null = null): Promise<ModelsPayload> {
  const response = await fetch(modelsPath(kind), { credentials: "same-origin" });
  let body: Record<string, unknown>;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    throw new ApiError(`the server answered ${response.status} with nothing readable`);
  }
  if (!response.ok || body["ok"] !== true) {
    throw new ApiError(String(body["error"] ?? response.status));
  }
  return body as unknown as ModelsPayload;
}
